
import { useState, useEffect } from "react";
import { CameraInventory, CameraFormValues } from "@/types/inventory";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";

export function useCameraInventory() {
  const { t } = useTranslation();
  const [cameras, setCameras] = useState<CameraInventory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchCameras = async () => {
    try {
      setIsLoading(true);
      setIsError(false);

      const { data, error } = await supabase
        .from("camera_inventory")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Assert that the data is a CameraInventory array using type guards instead of assertion
      if (!data || !Array.isArray(data)) {
        throw new Error("Invalid data format");
      }
      
      setCameras(data as CameraInventory[]);
    } catch (error) {
      console.error("Error fetching cameras:", error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCameras();
  }, []);

  const addCamera = async (cameraData: CameraFormValues) => {
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("camera_inventory")
        .insert([
          {
            brand: cameraData.brand,
            model: cameraData.model,
            camera_type: cameraData.camera_type,
            format: cameraData.format || null,
            mount: cameraData.mount || null,
            status: cameraData.status,
            serial_number: cameraData.serial_number || null,
            purchase_date: cameraData.purchase_date || null,
            purchase_price: cameraData.purchase_price ? parseFloat(cameraData.purchase_price) : null,
            notes: cameraData.notes || null,
            adapter_enabled: cameraData.adapter_enabled || false,
            adapter_mounts: cameraData.adapter_mounts || null,
            user_id: user.id // Add the user_id field
          }
        ])
        .select();

      if (error) throw error;

      toast.success(t("camera_added_successfully"));
      await fetchCameras();
      return data;
    } catch (error: any) {
      toast.error(`${t("error_adding_camera")}: ${error.message}`);
      throw error;
    }
  };

  return { cameras, isLoading, isError, addCamera, refreshCameras: fetchCameras };
}
